// src/components/MacroTrendsChart.jsx
import React from 'react';

const MacroTrendsChart = ({ macroData }) => {
  const cpi = macroData?.cpi_yoy?.value ?? null;
  const coreCpi = macroData?.core_cpi_yoy?.value ?? null;

  // GDP latest + previous quarter (this is the key change)
  const gdpLatest = macroData?.real_gdp_growth?.value ?? null;
  const gdpPrev = macroData?.real_gdp_growth?.prevValue ?? null;

  const makeScale = (values) => {
    const nums = values.filter((v) => v !== null && !Number.isNaN(v));
    if (!nums.length) return { min: 0, max: 5 };
    const max = Math.max(...nums, 2);
    const min = Math.min(...nums, 0);
    return { min, max: max === min ? max + 1 : max };
  };

  const inflationBars = [
    { id: 'target', label: 'Fed goal', value: 2 },
    { id: 'cpi', label: 'CPI YoY', value: cpi },
    { id: 'core', label: 'Core CPI', value: coreCpi },
  ];

  // ✅ Replace 0% bar with Previous vs Latest quarter
  const gdpBars = [
    { id: 'prev', label: 'Prev quarter', value: gdpPrev },
    { id: 'latest', label: 'Latest quarter', value: gdpLatest },
  ];

  const inflationScale = makeScale(inflationBars.map((b) => b.value));
  const gdpScale = makeScale(gdpBars.map((b) => b.value));

  const barHeight = (value, scale) => {
    if (value === null || Number.isNaN(value)) return 24;
    const { min, max } = scale;
    const range = max - min || 1;
    const normalized = ((value - min) / range) * 120 + 30;
    return Math.min(Math.max(normalized, 24), 160);
  };

  const formatPct = (v) =>
    v === null || Number.isNaN(v) ? '—' : `${v.toFixed(2)}%`;

  // Optional: show “momentum” in the caption
  const gdpDelta =
    gdpLatest != null && gdpPrev != null && !Number.isNaN(gdpLatest) && !Number.isNaN(gdpPrev)
      ? gdpLatest - gdpPrev
      : null;

  const momentumText =
    gdpDelta == null
      ? 'Momentum needs two quarters of data.'
      : gdpDelta >= 0
        ? `Growth is accelerating vs last quarter (+${gdpDelta.toFixed(2)}pp).`
        : `Growth is slowing vs last quarter (${gdpDelta.toFixed(2)}pp).`;

  return (
    <div className="chart-row">
      {/* Inflation mini bar chart */}
      <div className="chart-card">
        <div className="chart-title">Inflation (CPI vs Core vs 2% target)</div>
        <div className="yield-chart-container">
          <div className="yield-chart-bars">
            {inflationBars.map((bar) => (
              <div key={bar.id} className="yield-bar-container">
                <div
                  className="yield-bar"
                  style={{ height: `${barHeight(bar.value, inflationScale)}px` }}
                />
                <div className="yield-label">{bar.label}</div>
                <div className="yield-value">{formatPct(bar.value)}</div>
              </div>
            ))}
          </div>
        </div>
        <p className="macro-chart-caption">
          Bars above 2% mean inflation is running hotter than the Fed&apos;s goal. When CPI and
          core drop closer to the target, policy can turn less restrictive.
        </p>
      </div>

      {/* ✅ GDP mini bar chart (Prev vs Latest) */}
      <div className="chart-card">
        <div className="chart-title">Real GDP Growth (prev vs latest quarter)</div>
        <div className="yield-chart-container">
          <div className="yield-chart-bars">
            {gdpBars.map((bar) => (
              <div key={bar.id} className="yield-bar-container">
                <div
                  className="yield-bar"
                  style={{ height: `${barHeight(bar.value, gdpScale)}px` }}
                />
                <div className="yield-label">{bar.label}</div>
                <div className="yield-value">{formatPct(bar.value)}</div>
              </div>
            ))}
          </div>
        </div>
        <p className="macro-chart-caption">
          {momentumText} Investors care less about one print and more about whether growth is
          speeding up or cooling down.
        </p>
      </div>
    </div>
  );
};

export default MacroTrendsChart;